import React, { useEffect } from "react";
import { useLandscapePhone } from "../lobby/start/hooks";
import { flavourInfo, type FlavourId, type Shape } from "./roomRules";
import s from "./rooms.module.scss";
import "./setup.scss";

// The shared pieces of the Game Rooms screens (join, room, rules, local): the
// page chrome (backdrop, stage, header), the icons and the small controls.

// ---------- icons (24 grid, 2px strokes, round joins) ----------
const svgProps = { viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: 2, strokeLinecap: "round" as const, strokeLinejoin: "round" as const, "aria-hidden": true };
export const Globe = () => (<svg {...svgProps}><circle cx="12" cy="12" r="9" /><path d="M3 12h18M12 3c3 3.2 3 14.8 0 18M12 3c-3 3.2-3 14.8 0 18" /></svg>);
export const Lock = () => (<svg {...svgProps}><rect x="5" y="10" width="14" height="10" rx="2" /><path d="M8 10V7a4 4 0 018 0v3" /></svg>);
export const Keys = () => (<svg {...svgProps}><rect x="2" y="6" width="20" height="12" rx="2" /><path d="M6 10h.01M10 10h.01M14 10h.01M18 10h.01M7 14h10" /></svg>);
export const Plus = () => (<svg {...svgProps}><path d="M12 5v14M5 12h14" /></svg>);
export const Chev = ({ className }: { className?: string }) => (<svg {...svgProps} className={className}><path d="M9 6l6 6-6 6" /></svg>);
export const ChevL = () => (<svg {...svgProps}><path d="M15 6l-6 6 6 6" /></svg>);
export const Crown = () => (<svg {...svgProps}><path d="M4 8l4 3.5L12 5l4 6.5L20 8l-1.6 10H5.6L4 8z" fill="currentColor" fillOpacity="0.25" /></svg>);
export const Play = () => (<svg {...svgProps}><path d="M7 5l12 7-12 7V5z" fill="currentColor" fillOpacity="0.25" /></svg>);
export const Link = () => (<svg {...svgProps}><path d="M10 14a4 4 0 005.7 0l3-3a4 4 0 00-5.7-5.7l-1 1M14 10a4 4 0 00-5.7 0l-3 3a4 4 0 005.7 5.7l1-1" /></svg>);
export const Check = () => (<svg {...svgProps} strokeWidth={2.6}><path d="M5 12.5l4.5 4.5L19 7.5" /></svg>);
export const Chat = () => (<svg {...svgProps}><path d="M4 5h16v11H10l-5 4v-4H4V5z" /></svg>);
export const Users = () => (<svg {...svgProps}><circle cx="9" cy="8" r="3.5" /><path d="M2.5 20a6.5 6.5 0 0113 0M16 4.8a3.5 3.5 0 010 6.4M18 14.6a6.5 6.5 0 013.5 5.4" /></svg>);
export const Flag = () => (<svg {...svgProps}><path d="M5 21V4M5 4h12l-2.5 4L17 12H5" /></svg>);
export const Sliders = () => (<svg {...svgProps}><path d="M4 7h9M17 7h3M4 17h3M11 17h9" /><circle cx="15" cy="7" r="2" /><circle cx="9" cy="17" r="2" /></svg>);
export const Bolt = () => (<svg {...svgProps}><path d="M13 3L5 13.5h6L10 21l8-10.5h-6L13 3z" fill="currentColor" fillOpacity="0.25" /></svg>);
export const Target = () => (<svg {...svgProps}><circle cx="12" cy="12" r="9" /><circle cx="12" cy="12" r="4.5" /><path d="M12 12h.01" /></svg>);
export const Flame = () => (<svg {...svgProps}><path d="M12 3c1 4 5 5.5 5 10.5a5 5 0 01-10 0c0-2 1-3.2 2-4.2.4 1.6 1.2 2.4 2 2.7C10.5 9 11 6 12 3z" fill="currentColor" fillOpacity="0.25" /></svg>);
export const Duel = () => (<svg {...svgProps}><path d="M4 4l9 9M4 4v4M4 4h4M20 4l-9 9M20 4v4M20 4h-4M7 14l-3 3 3 3 3-3M17 14l3 3-3 3-3-3" /></svg>);
export const MapIcon = () => (<svg {...svgProps}><rect x="3" y="6" width="18" height="12" rx="6" /></svg>);
export const Kick = () => (<svg {...svgProps}><path d="M7 7l10 10M17 7L7 17" /></svg>);
export const Qr = () => (<svg {...svgProps}><rect x="4" y="4" width="6" height="6" rx="1" /><rect x="14" y="4" width="6" height="6" rx="1" /><rect x="4" y="14" width="6" height="6" rx="1" /><path d="M14 14h2v2M20 14v2M14 20h2M18 18h2v2" /></svg>);
const Cup = () => (<svg {...svgProps}><path d="M6 8h9v11a2 2 0 01-2 2H8a2 2 0 01-2-2V8zM15 11h2a2.5 2.5 0 010 5h-2M6 8c-.5-2 1-3.5 3-3 .3-1.7 2.7-1.7 3 0 2-.5 3.5 1 3 3" /></svg>);
const Burger = () => (<svg {...svgProps}><path d="M4 11a8 5.5 0 0116 0zM4 14.5h16M5 18h14a1.5 1.5 0 000-3H5a1.5 1.5 0 000 3z" /></svg>);

const MODE_ICONS: Record<string, () => React.ReactElement> = { bolt: Bolt, target: Target, flame: Flame, duel: Duel, sliders: Sliders, users: Users, cup: Cup, burger: Burger };
export function FlavourIcon({ id }: { id: FlavourId }) {
  const I = MODE_ICONS[flavourInfo(id).icon] || Bolt;
  return <I />;
}

// The setup pages' shell styles every button's hover with a white overlay
// (app.scss, .app-main--wide); "sx-plain" opts a button out, so these keep the
// hover this design gives them.
export function B({ className, type, ...rest }: React.ButtonHTMLAttributes<HTMLButtonElement>) {
  return <button type={type || "button"} className={`sx-plain ${className || ""}`} {...rest} />;
}

const hexPts = (cx: number, cy: number, r: number) =>
  Array.from({ length: 6 }, (_, k) => {
    const a = (Math.PI / 180) * (60 * k - 90);
    return `${(cx + r * Math.cos(a)).toFixed(1)},${(cy + r * Math.sin(a)).toFixed(1)}`;
  }).join(" ");
// An icon on a hexagon tile, tinted with the flavour colour (currentColor).
export const Hex = ({ children, className }: { children: React.ReactNode; className?: string }) => (
  <span className={`${s.hex} ${className || ""}`}>
    <svg viewBox="0 0 64 64" aria-hidden="true"><polygon points={hexPts(32, 32, 29)} fill="currentColor" fillOpacity="0.12" stroke="currentColor" strokeOpacity="0.5" strokeWidth="2" strokeLinejoin="round" /></svg>
    {children}
  </span>
);
// A small arena outline with a couple of trails in it.
export const MapGlyph = ({ shape }: { shape: Shape }) => (
  <svg className={s.mapGlyph} viewBox={shape === "square" || shape === "circle" ? "0 0 26 26" : "0 0 48 26"} fill="none" aria-hidden="true">
    {shape === "surprise" ? (<>
      <rect x="2" y="2" width="44" height="22" rx="11" stroke="currentColor" strokeWidth="2" strokeDasharray="4 4" opacity="0.8" />
      <path d="M20.5 10a3.6 3.6 0 117 1.2c0 2.2-3.2 2.4-3.2 4.6M24.3 19.6h.01" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    </>) : shape === "square" || shape === "circle" ? (
      <rect x="2" y="2" width="22" height="22" rx={shape === "circle" ? 11 : 3} stroke="currentColor" strokeWidth="2" />
    ) : (<>
      <rect x="2" y="2" width="44" height="22" rx={shape === "stadium" ? 11 : 3} stroke="currentColor" strokeWidth="2" />
      <path d="M9 17c5 1 6-8 12-7s4 6 9 5" stroke="#19f0b0" strokeWidth="1.8" strokeLinecap="round" />
      <path d="M38 8c-4 0-5 4-9 3" stroke="#ff6ad5" strokeWidth="1.8" strokeLinecap="round" />
    </>)}
  </svg>
);

// ---------- small controls ----------
export function Seg<T extends string | number>({ value, options, onPick, tall }: { value: T | null; options: { v: T; label: React.ReactNode; sub?: string }[]; onPick: (v: T) => void; tall?: boolean }) {
  return (
    <div className={`${s.seg} ${tall ? s.segTall : ""}`} role="radiogroup">
      {options.map((o) => (
        <B key={String(o.v)} role="radio" aria-checked={o.v === value} className={o.v === value ? s.segOn : ""} onClick={() => onPick(o.v)}>
          <span>{o.label}</span>{o.sub && <small>{o.sub}</small>}
        </B>
      ))}
    </div>
  );
}
export function Stepper({ value, onChange, min, max, label, small }: { value: React.ReactNode; onChange: (d: number) => void; min?: boolean; max?: boolean; label: string; small?: boolean }) {
  return (
    <div className={`${s.stepper} ${small ? s.stepperSm : ""}`}>
      <B onClick={() => onChange(-1)} disabled={min} aria-label={`Less: ${label}`}>−</B>
      <b>{value}</b>
      <B onClick={() => onChange(1)} disabled={max} aria-label={`More: ${label}`}>+</B>
    </div>
  );
}

// ---------- the page chrome ----------
// The backdrop, the stage card and its header. On a PC it sits inside the app
// shell under the navbar; on a phone held sideways it is the whole screen, and
// the shell's navbar and footer are hidden with a class on <body>.
export function RoomsPage({ title, sub, onBack, backLabel = "Back", right, children, stageKey, over, wide, after }: {
  title: React.ReactNode;
  after?: React.ReactNode; // beside the title (the room: who can join)
  sub?: string; onBack?: () => void; backLabel?: string; right?: React.ReactNode; children: React.ReactNode; stageKey?: string;
  wide?: boolean; // the room itself: three columns want the window's width
  over?: React.ReactNode; // a layer over the whole stage (the phone's chat panel)
}) {
  const land = useLandscapePhone();
  useEffect(() => {
    if (!land) return;
    document.body.classList.add("rooms-app");
    return () => document.body.classList.remove("rooms-app");
  }, [land]);
  return (
    <div className={`${s.root} ${land ? s.land : s.pc}`}>
      <div className={s.backdrop} aria-hidden="true">
        <div className={s.aurora}><i className={s.blobTeal} /><i className={s.blobGreen} /><i className={s.blobViolet} /></div>
        <div className={s.grain} />
        <div className={s.vignette} />
      </div>
      <div className={`${s.stage} ${wide ? s.stageWide : ""}`}>
        <header className={s.head}>
          {onBack && <B className={s.backBtn} onClick={onBack} aria-label={backLabel} title={backLabel}><ChevL /></B>}
          <h1>{title}</h1>
          {after}
          <span className={s.sub}>{sub}</span>
          {right}
        </header>
        <div className={s.body} key={stageKey}>{children}</div>
        {over}
      </div>
    </div>
  );
}
export function useLand(): boolean {
  return useLandscapePhone();
}
